import type { Patch } from "@freightsimple/shared/types";
import { AutoComplete } from "@freightsimple/ui-core/Controls/AutoComplete";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { describeLocationType } from "@/Data/describeLocationType";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";

import { countryCodeFromPostalCode } from "../Validators/locationsErrorMessageForPostalCode";
import type { AddressErrors } from "../Validators/locationsErrorMessagesForAddress";
import distributionWarehouseBrands from "@/GeneratedFixtures/DistributionWarehouseBrands.json";

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
function generateBusinessNameWarningMessage(
  businessName: string,
  locationType: LocationType,
): ErrorMessageType {
  const lcbn = businessName.toLowerCase();

  if (lcbn.includes("farm") && locationType !== LocationType.Farm) {
    return "⚠️ This might be a Farm. Please specify Farm when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (
    lcbn.includes("school") &&
    locationType !== LocationType.UniversityCollege
  ) {
    return "⚠️ This might be a School. Please specify Educational when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (
    lcbn.includes("college") &&
    locationType !== LocationType.UniversityCollege
  ) {
    return "⚠️ This might be a College. Please specify Educational when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (
    lcbn.includes("university") &&
    locationType !== LocationType.UniversityCollege
  ) {
    return "⚠️ This might be a University. Please specify Educational when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("correctional") && locationType !== LocationType.Prison) {
    return "⚠️ This might be a Prison. Please specify Prison when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("prison") && locationType !== LocationType.Prison) {
    return "⚠️ This might be a Prison. Please specify Prison when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("military") && locationType !== LocationType.Military) {
    return "⚠️ This might be a Military base. Please specify Military when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("army") && locationType !== LocationType.Military) {
    return "⚠️ This might be a Military base. Please specify Military when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("airport") && locationType !== LocationType.Airport) {
    return "⚠️ This might be an Airport. Please specify Airport when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("port") && locationType !== LocationType.Port) {
    return "⚠️ This might be a Port. Please specify Port when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("church") && locationType !== LocationType.ReligiousSite) {
    return "⚠️ This might be a Church. Please specify Religious Site when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("mosque") && locationType !== LocationType.ReligiousSite) {
    return "⚠️ This might be a Mosque. Please specify Religious Site when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("temple") && locationType !== LocationType.ReligiousSite) {
    return "⚠️ This might be a Temple. Please specify Religious Site when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("mine") && locationType !== LocationType.Mine) {
    return "⚠️ This might be a Mine. Please specify Mine when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("storage") && locationType !== LocationType.SelfStorage) {
    return "⚠️ This might be a Self Storage facility. Please specify Self Storage when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("hospital") && locationType !== LocationType.Medical) {
    return "⚠️ This might be a Hospital. Please specify Medical when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("expo") && locationType !== LocationType.TradeShow) {
    return "⚠️ This might be an Expo/Trade Show. Please specify Trade Show when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("conference") && locationType !== LocationType.TradeShow) {
    return "⚠️ This might be a Conference venue. Please specify Trade Show when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  if (lcbn.includes("store") && locationType !== LocationType.Retail) {
    return "⚠️ This might be a Retail location. Please specify Retail when quoting to get an accurate rate. Additional charges may apply if the wrong location type is specified.";
  }

  function potentialDistributionWarehouseName(): string | undefined {
    const normalize = (str: string) =>
      str.replace(/[^a-zA-Z]/g, "").toLowerCase();

    const formatDisplayName = (name: string) => {
      const article = /^[AEIOU]/i.test(name) ? "an" : "a";
      return `${article} ${name}`;
    };

    const terms = distributionWarehouseBrands.flatMap((brand) => {
      const option = [[brand.identifier, formatDisplayName(brand.displayName)]];
      return [...option];
    });

    const normalizedLcbn = normalize(lcbn);

    return terms.find((o) => normalizedLcbn.includes(o[0] ?? ""))?.[1];
  }

  const pdwn = potentialDistributionWarehouseName();
  if (pdwn && locationType !== LocationType.DistributionWarehouse) {
    return `⚠️ This might be ${pdwn} location. Please specify Distribution Warehouse when quoting to get an accurate rate. Additional charges may apply is the wrong location type is specified.`;
  }

  return undefined;
}

interface AddressInputProps {
  businessName: string;
  businessNameChanged: (_: string) => void;
  address?: Patch<Address> | undefined;
  addressChanged: (_: Patch<Address>) => void;
  locationType: LocationType;
  distributionWarehouseBrand: string | undefined;
  enabled: boolean;
  addressErrorMessages: AddressErrors;
  businessNameErrorMessages: ErrorMessageType;
  forceValidation: boolean;
  addressOptions: Array<string>;
  displayName?: string | undefined;
  allowCityAndPostalCodeEditing: boolean;
  warnAboutPotentialBusinessNameErrors: boolean;
  accountNumber?: string | undefined;
  accountNumberChanged?: ((_: string) => void) | undefined;
  accountNumberErrorMessages?: ErrorMessageType | undefined;
  showAccountNumberInput?: boolean | undefined;
  customBusinessNameLabel?: string | undefined;
  /** Character caps for the text fields. Only callers that need them pass
   *  this — the customs broker form does, because its saved values are shown
   *  on a fixed-width card. */
  maxLengths?:
    | {
        businessName: number;
        accountNumber: number;
        addressLine: number;
        addressLine2: number;
        city: number;
        postalCode: number;
      }
    | undefined;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
const LocationsAddressInput: React.FC<AddressInputProps> = (
  props: AddressInputProps,
) => {
  const disabled = !props.enabled;

  const {
    forceValidation,
    addressErrorMessages,
    businessNameErrorMessages,
    allowCityAndPostalCodeEditing,
  } = props;

  const businessNameWarningMessage = props.warnAboutPotentialBusinessNameErrors
    ? generateBusinessNameWarningMessage(props.businessName, props.locationType)
    : undefined;

  const addressLabel =
    props.displayName === undefined
      ? "Address"
      : `${props.displayName} Address`;

  function describeBusinessName() {
    if (props.customBusinessNameLabel) {
      return <>{props.customBusinessNameLabel}</>;
    }

    if (
      props.locationType === LocationType.DistributionWarehouse &&
      props.distributionWarehouseBrand
    ) {
      const brand = distributionWarehouseBrands.find(
        (b) => b.identifier === props.distributionWarehouseBrand,
      );
      if (brand) {
        return <>{brand.displayName} Warehouse Name</>;
      }
    }

    const substring = `Specified Location Type : ${describeLocationType(props.locationType, props.distributionWarehouseBrand)}`;
    if (props.locationType === LocationType.TradeShow) {
      return <>Trade Show Name</>;
    }

    if (props.locationType === LocationType.Residential) {
      return <>Business / Customer Name ({substring})</>;
    }
    return <>Business Name ({substring})</>;
  }

  return (
    <ShowFormErrors show={forceValidation}>
      <Stack gap={4} align="stretch">
        <FormItem
          label={describeBusinessName()}
          error={businessNameErrorMessages}
          warning={businessNameWarningMessage}
          fillWidth
        >
          <TextInput
            name="business_name"
            value={props.businessName || ""}
            disabled={disabled}
            onChange={(e) => props.businessNameChanged(e.target.value)}
            width="full"
            autoComplete="organization"
            maxLength={props.maxLengths?.businessName}
          />
        </FormItem>
        {props.showAccountNumberInput && (
          <FormItem
            label="Customs Broker Account Number"
            error={props.accountNumberErrorMessages}
            fillWidth
          >
            <TextInput
              name="customs_broker_account_number"
              value={props.accountNumber || ""}
              disabled={disabled}
              onChange={(e) => props.accountNumberChanged?.(e.target.value)}
              width="full"
              maxLength={props.maxLengths?.accountNumber}
            />
          </FormItem>
        )}
        <FormItem
          label={addressLabel}
          error={addressErrorMessages.addressLine}
          fillWidth
        >
          <AutoComplete
            name="address_line"
            value={props.address?.addressLine || ""}
            disabled={disabled}
            onChange={(value: string) =>
              props.addressChanged({
                addressLine: value,
              })
            }
            // https://stackoverflow.com/questions/12374442/chrome-ignores-autocomplete-off
            autoComplete="nope"
            options={props.addressOptions}
            maxLength={props.maxLengths?.addressLine}
          />
        </FormItem>
        <FormItem
          label="Other details (eg. Unit / Floor / Door)"
          error={addressErrorMessages.addressLine2}
          fillWidth
        >
          <TextInput
            name="address_line_2"
            value={props.address?.addressLine2 || ""}
            disabled={disabled}
            onChange={(e) =>
              props.addressChanged({
                addressLine2: e.target.value,
              })
            }
            width="full"
            maxLength={props.maxLengths?.addressLine2}
          />
        </FormItem>
        {/* City gets the room; the 2:1:1 ratio mirrors the legacy
            250:115:115 split. */}
        <Grid columns={[2, 1, 1]} gap="sm" minColumnWidth={90}>
          <FormItem label="City" error={addressErrorMessages.city} fillWidth>
            <TextInput
              name="city"
              value={props.address?.city || ""}
              disabled={disabled || !allowCityAndPostalCodeEditing}
              onChange={(e) =>
                props.addressChanged({
                  city: e.target.value,
                })
              }
              width="full"
              maxLength={props.maxLengths?.city}
            />
          </FormItem>
          <FormItem
            label="State or Province"
            error={addressErrorMessages.stateOrProvinceCode}
            fillWidth
          >
            <TextInput
              name="state_or_province_code"
              value={props.address?.stateOrProvinceCode || ""}
              disabled={disabled || !allowCityAndPostalCodeEditing}
              onChange={(e) =>
                props.addressChanged({
                  stateOrProvinceCode: e.target.value
                    .replace(/[^A-Za-z]/g, "")
                    .slice(0, 2),
                })
              }
              width="full"
            />
          </FormItem>
          <FormItem
            label="Postal Code"
            error={addressErrorMessages.postalCode}
            fillWidth
          >
            <TextInput
              name="postal_code"
              value={props.address?.postalCode || ""}
              disabled={disabled || !allowCityAndPostalCodeEditing}
              onChange={function (e) {
                const value = e.target.value;
                const countryCode = countryCodeFromPostalCode(value);
                if (countryCode !== undefined) {
                  props.addressChanged({
                    postalCode: value,
                    countryCode,
                  });
                } else {
                  props.addressChanged({
                    postalCode: value,
                  });
                }
              }}
              width="full"
              maxLength={props.maxLengths?.postalCode}
            />
          </FormItem>
        </Grid>
      </Stack>
    </ShowFormErrors>
  );
};
export default LocationsAddressInput;
